<template>
    <v-form
        :ref="formRef"
        v-model="form.valid"
        lazy-validation
    >
        <v-dialog v-model="dialogState" fullscreen hide-overlay @keydown.esc="close()">
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    :loading="loading"
                    :disabled="loading"
                    :dark="!loading"
                    class="mr-1"
                    :color="buttonColor"
                    v-bind="attrs"
                    v-on="on"
                    :outlined="!isNew"
                >
                    <v-icon v-if="!isNew" class="mr-1">edit</v-icon>
                    {{ buttonLabel }}
                </v-btn>
            </template>

            <v-card min-height="100vh">
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click.native="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>{{ form.title }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn
                            dark
                            text
                            :disabled="!form.valid"
                            :loading="btnDisable"
                            @click.native="save()"
                        >{{ $t('common.save') }}</v-btn>
                    </v-toolbar-items>
                </v-toolbar>

                <v-container>
                    <v-card-title>
                        <v-subheader>{{ $t('corporate.administration.form.data') }}</v-subheader>
                    </v-card-title>

                    <v-divider></v-divider>

                    <v-progress-linear :indeterminate="true" v-show="form.loading"></v-progress-linear>

                    <div v-show="!form.loading">
                        <v-row no-gutters class="pt-3 pb-1">
                            <v-col cols="4">
                                <input-date-picker :setup.sync="form.date"></input-date-picker>
                            </v-col>
                        </v-row>
                        <v-row no-gutters>
                            <v-col cols="12" class="pb-1">
                                <div class="d-flex justify-space-between align-center">
                                    <span class="title">
                                        <v-icon>group</v-icon>
                                        {{ `${form.memberType} ${$t('corporate.administration.form.admitted')}` }}
                                    </span>
                                    <v-btn
                                        color="primary"
                                        @click.native="addMember()"
                                    >
                                        {{ $t('common.add') }}
                                    </v-btn>
                                </div>
                                <v-divider class="mt-4"></v-divider>
                            </v-col>
                        </v-row>
                        <div>
                            <template v-if="membersNotDeprivedCount <= 0">
                                <div class="text-center"><span style="color: red">{{ $t('corporate.administration.form.empty') }}</span></div>
                            </template>
                            <template v-else v-for="(item, index) in membersNotDeprived">
                                <v-row v-if="item != null" :key="index" align="center">
                                    <v-col cols="6" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="item.name"
                                            :label="form.labels.name"
                                            :rules="form.rules.member"
                                            required
                                            filled
                                            dense
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="6" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="item.position"
                                            :label="form.labels.job"
                                            :rules="form.rules.position"
                                            required
                                            filled
                                            dense
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1">
                                        <input-date-picker :setup.sync="form.datePickers.election[index]"></input-date-picker>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1">
                                        <input-date-picker :setup.sync="form.datePickers.mandate[index]"></input-date-picker>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1 text-right">
                                        <v-switch
                                            flat
                                            inset
                                            dense
                                            hide-details
                                            v-model="form.datePickers.mandate[index].switch"
                                            :label="$t('corporate.administration.form.mandate.label')"
                                            class="ml-4 mt-0"
                                            @change="changeDPState(index)"
                                        ></v-switch>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1 text-right">
                                        <v-btn
                                            small
                                            color="purple"
                                            class="mr-1"
                                            dark
                                            @click.prevent="showDeprivedDialog(index, item)"
                                        >
                                            {{ $t('corporate.administration.form.dismiss') }}
                                        </v-btn>
                                        <v-btn
                                            small
                                            color="error"
                                            class="ml-1"
                                            @click.prevent="deleteMember(index, item)"
                                        >
                                            {{ $t('common.remove') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="12" class="pb-1">
                                        <v-divider class="d-flex mt-4"></v-divider>
                                    </v-col>
                                </v-row>
                            </template>
                        </div>
                        <template v-if="membersDeprivedCount > 0">
                            <v-row no-gutters>
                                <v-col cols="12" class="pb-1">
                                    <div class="d-flex justify-space-between align-center">
                                        <span class="title pt-2">
                                            <span class="title">
                                                <v-icon>group</v-icon>
                                                {{ `${form.memberType} ${$t('corporate.administration.form.destitute')}` }}
                                            </span>
                                        </span>
                                    </div>
                                    <v-divider class="mt-4"></v-divider>
                                </v-col>
                            </v-row>
                            <template v-for="(item, index) in membersDeprived">
                                <v-row v-if="item != null" :key="index" align="center">
                                    <v-col cols="3" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="item.name"
                                            :label="form.labels.name"
                                            :rules="form.rules.member"
                                            required
                                            filled
                                            dense
                                            hide-details
                                            readonly
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="item.position"
                                            :label="form.labels.job"
                                            :rules="form.rules.position"
                                            required
                                            filled
                                            dense
                                            hide-details
                                            readonly
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1">
                                        <input-date-picker :setup.sync="form.datePickers.deprived[index]"></input-date-picker>
                                    </v-col>
                                    <v-col cols="3" class="pt-1 pb-1 text-right">
                                        <v-btn
                                            small
                                            color="green"
                                            class="mr-1"
                                            dark
                                            @click.prevent="undeprivedMember(index, item)"
                                        >
                                            {{ $t('corporate.administration.form.refund') }}
                                        </v-btn>
                                        <v-btn
                                            small
                                            color="error"
                                            class="ml-1"
                                            @click.prevent="deleteMember(index, item)"
                                        >
                                            {{ $t('common.remove') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="12" class="pb-1">
                                        <v-divider class="d-flex mt-4"></v-divider>
                                    </v-col>
                                </v-row>
                            </template>
                        </template>
                    </div>
                </v-container>
            </v-card>
        </v-dialog>
        <v-dialog
            v-if="deprived.item"
            v-model="deprived.dialog"
            persistent
            max-width="500px"
        >
            <v-card>
                <v-card-title>
                    <span>Destituição</span>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col>
                            <input-date-picker :setup.sync="form.datePickers.deprived[deprived.index]"></input-date-picker>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        class="mr-1"
                        color="error"
                        @click.prevent="closeDeprivedDialog()"
                    >
                        {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn
                        small
                        class="ml-1"
                        color="primary"
                        @click.prevent="deprivedMember(deprived.index, deprived.item)"
                    >
                        {{ $t('common.confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-form>
</template>

<script>
import API from '@/api'
import InputDatePicker from '@/components/InputDatePicker'

export default {
    name: 'modal-board-members',
    props: ['loading', 'companyInfo', 'currentBoardMembers', 'isNew', 'type'],
    components: {
        InputDatePicker
    },
    directives: {},
    data: function () {
        return {
            dialogState: false,
            deprived: {
                dialog: false,
                index: undefined,
                item: undefined
            },
            btnDisable: false,
            form: {
                ref: '',
                loading: true,
                valid: false,
                title: this.$t('corporate.administration.form.titleNew'),
                memberType: this.$t('corporate.administration.form.memberTypeLTDA'),
                labels: {
                    name: this.$t('corporate.administration.form.member.label'),
                    job: this.$t('corporate.administration.form.position.label')
                },
                date: {
                    label: this.$t('corporate.administration.form.date.label'),
                    rule: [
                        v => !!v || this.$t('corporate.administration.form.date.required')
                    ],
                    required: true,
                    readonly: false,
                    filled: true,
                    dateFormatted: undefined
                },
                datePickers: {
                    election: [],
                    mandate: [],
                    deprived: []
                },
                rules: {
                    member: [
                        v => !!v || this.$t('corporate.administration.form.member.required'),
                        v => (v && v.length >= 3) || this.$t('corporate.administration.form.member.length')
                    ],
                    position: [
                        v => !!v || this.$t('corporate.administration.form.position.required'),
                        v => (v && v.length >= 3) || this.$t('corporate.administration.form.position.length')
                    ],
                    mandate: [
                        v => !!v || this.$t('corporate.administration.form.mandate.required'),
                        v => (v && v.length >= 3) || this.$t('corporate.administration.form.mandate.length')
                    ]
                }
            },
            baseBoardMembers: undefined,
            boardMembers: {
                objectId: undefined,
                client: undefined,
                createdBy: undefined,
                date: undefined,
                type: undefined,
                isCurrent: undefined,
                members: []
            }
        }
    },
    watch: {
        dialogState: function (val, oldVal) {
            if (val === true) {
                this.init()
            }
        }
    },
    mounted () {},
    computed: {
        buttonColor () {
            return (!this.isNew) ? 'secondary' : 'primary'
        },
        buttonLabel () {
            return (!this.isNew) ? this.$t('common.edit') : this.$t('common.add')
        },
        formRef () {
            return (!this.isNew) ? 'formBoardMembersEdit' : 'formBoardMembersAdd'
        },
        membersNotDeprived () {
            return this.boardMembers.members.map(member => { if (!member.deprivedStatus) return member })
        },
        membersDeprived () {
            return this.boardMembers.members.map(member => { if (member.deprivedStatus) return member })
        },
        membersNotDeprivedCount () {
            return this.boardMembers.members.reduce((accumulator, element) => {
                if (element.deprivedStatus === false) {
                    return (accumulator += 1)
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        },
        membersDeprivedCount () {
            return this.boardMembers.members.reduce((accumulator, element) => {
                if (element.deprivedStatus === true) {
                    return (accumulator += 1)
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        }
    },
    methods: {
        init () {
            this.baseBoardMembers = JSON.parse(JSON.stringify(this.boardMembers))
            this.form.datePickers.election = []
            this.form.datePickers.mandate = []
            if (!this.isNew) {
                this.$refs.formBoardMembersEdit.reset()
                this.form.title = this.$t('corporate.administration.form.titleDetail')
                this.boardMembers = JSON.parse(JSON.stringify(this.currentBoardMembers))
                this.form.date.dateFormatted = this.$options.filters.momentFormatDate(this.boardMembers.date, 'L')
            } else {
                this.$refs.formBoardMembersAdd.reset()
                if (this.currentBoardMembers !== undefined) {
                    this.boardMembers.members = JSON.parse(JSON.stringify(this.currentBoardMembers.members))
                    this.removeDeprivedMember()
                }
            }
            if (this.type !== 1) {
                this.form.memberType = this.$t('corporate.administration.form.memberTypeSA')
            }
            this.setDatePickers()
            this.loadDatePickers()
            this.form.loading = false
        },
        setDatePickers () {
            for (let index = 0; index < this.boardMembers.members.length; index++) {
                this.addDatePicker()
            }
        },
        addDatePicker () {
            this.form.datePickers.election.unshift(
                {
                    label: this.$t('corporate.administration.form.election.label'),
                    rule: [
                        v => !!v || this.$t('corporate.administration.form.election.required')
                    ],
                    required: true,
                    readonly: false,
                    filled: true,
                    dateFormatted: undefined,
                    hideDetails: true,
                    disabled: false
                }
            )
            this.form.datePickers.mandate.unshift(
                {
                    label: this.$t('corporate.administration.form.mandate.label'),
                    rule: [
                        v => !!v || this.$t('corporate.administration.form.mandate.required')
                    ],
                    required: true,
                    readonly: false,
                    filled: true,
                    dateFormatted: undefined,
                    hideDetails: true,
                    disabled: false,
                    switch: false
                }
            )
            this.form.datePickers.deprived.unshift(
                {
                    label: this.$t('corporate.administration.form.deprived.label'),
                    rule: [
                        v => !!v || this.$t('corporate.administration.form.deprived.required')
                    ],
                    required: true,
                    readonly: false,
                    filled: true,
                    dateFormatted: undefined,
                    hideDetails: true,
                    disabled: false
                }
            )
        },
        loadDatePickers () {
            let regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
            for (let index = 0; index < this.boardMembers.members.length; index++) {
                if (regex.test(this.boardMembers.members[index].election)) {
                    this.form.datePickers.election[index].dateFormatted = this.boardMembers.members[index].election
                } else {
                    this.form.datePickers.election[index].dateFormatted = this.$options.filters.momentFormatDateNoISO(this.boardMembers.members[index].election, 'L')
                }

                if (regex.test(this.boardMembers.members[index].mandate)) {
                    this.form.datePickers.mandate[index].dateFormatted = this.boardMembers.members[index].mandate
                } else {
                    this.changeDPState(index)
                    this.form.datePickers.mandate[index].switch = true
                }

                if (regex.test(this.boardMembers.members[index].deprived)) {
                    this.form.datePickers.deprived[index].dateFormatted = this.boardMembers.members[index].deprived
                }
            }
        },
        async save () {
            let currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            let params = {
                date: undefined,
                isNew: true,
                objectId: undefined,
                clientId: this.$helpers.getCorporateClientId(this.$route.name, 'ClienteSocietarioAdministracao', this.$route.params.id),
                type: this.type,
                members: this.boardMembers.members,
                profile: currentUserCompany.objectId
            }

            if (this.currentBoardMembers !== undefined) {
                params.objectId = this.currentBoardMembers.objectId
            }

            if (!this.isNew) {
                if (!this.$refs.formBoardMembersEdit.validate()) {
                    this.releaseForm()
                    return
                }
                params.isNew = false
            } else {
                if (!this.$refs.formBoardMembersAdd.validate()) {
                    this.releaseForm()
                    return
                }
            }

            if (this.boardMembers.members.length <= 0) {
                return
            }

            params.date = this.$helpers.formatDatetoParse(this.form.date.dateFormatted)
            this.formatDateinMembers()
            this.form.loading = true
            this.btnDisable = true
            let endpoint = API.EndPoints.administrationRegister
            await new API(endpoint, params).request()
                .then(() => {
                    this.$emit('close-board-success-dialog')
                })
                .catch(error => {
                    if (error.status !== 0) alert(this.$t('common.error'))
                })
            this.close()
        },
        formatDateinMembers () {
            for (let index = 0; index < this.boardMembers.members.length; index++) {
                if (this.form.datePickers.election[index].dateFormatted !== undefined) {
                    let dateString = this.$helpers.formatDatetoParse(this.form.datePickers.election[index].dateFormatted)
                    this.boardMembers.members[index].election = dateString.iso
                }

                if (this.form.datePickers.mandate[index].disabled) {
                    this.boardMembers.members[index].mandate = this.$t('corporate.administration.form.mandate.undetermined')
                } else {
                    let dateString = this.$helpers.formatDatetoParse(this.form.datePickers.mandate[index].dateFormatted)
                    this.boardMembers.members[index].mandate = dateString.iso
                }

                if (this.boardMembers.members[index].deprivedStatus) {
                    let dateString = this.$helpers.formatDatetoParse(this.form.datePickers.deprived[index].dateFormatted)
                    this.boardMembers.members[index].deprived = dateString.iso
                }
            }
        },
        close () {
            this.form.date.dateFormatted = undefined
            this.btnDisable = false
            this.dialogState = false
            this.boardMembers = JSON.parse(JSON.stringify(this.baseBoardMembers))
        },
        addMember () {
            let memberObject = {
                name: '',
                position: '',
                election: '',
                mandate: '',
                deprived: '',
                deprivedStatus: false
            }
            this.boardMembers.members.unshift(memberObject)
            this.addDatePicker()
        },
        deleteMember (index, member) {
            this.boardMembers.members.splice(index, 1)
            this.form.datePickers.election.splice(index, 1)
            this.form.datePickers.mandate.splice(index, 1)
            this.form.datePickers.deprived.splice(index, 1)
        },
        releaseForm () {
            this.btnDisable = false
            this.form.valid = true
        },
        changeDPState (index) {
            this.form.datePickers.mandate[index].disabled = !this.form.datePickers.mandate[index].disabled
            this.form.datePickers.mandate[index].required = !this.form.datePickers.mandate[index].required
            if (this.form.datePickers.mandate[index].disabled === true) {
                this.form.datePickers.mandate[index].rule = []
            } else {
                this.form.datePickers.mandate[index].rule = [
                    v => !!v || this.$t('corporate.administration.form.mandate.required')
                ]
            }
            this.form.datePickers.mandate[index].dateFormatted = undefined
        },
        showDeprivedDialog (index, member) {
            this.deprived.dialog = true
            this.deprived.index = index
            this.deprived.item = member
        },
        deprivedMember (index, member) {
            if (this.form.datePickers.deprived[index] === undefined || this.form.datePickers.deprived[index].dateFormatted === undefined) {
                if (!this.isNew) {
                    this.$refs.formBoardMembersEdit.validate()
                } else {
                    this.$refs.formBoardMembersAdd.validate()
                }
                return
            }
            this.boardMembers.members[index].deprivedStatus = true
            this.boardMembers.members[index].deprived = this.form.datePickers.deprived[index].dateFormatted
            this.closeDeprivedDialog()
        },
        undeprivedMember (index, member) {
            this.boardMembers.members[index].deprivedStatus = false
            this.boardMembers.members[index].deprived = undefined
            this.form.datePickers.deprived[index].dateFormatted = undefined
        },
        closeDeprivedDialog () {
            this.deprived.dialog = false
            this.deprived.index = undefined
            this.deprived.item = undefined
        },
        removeDeprivedMember () {
            for (let index = 0; index < this.boardMembers.members.length; index++) {
                if (this.boardMembers.members[index].deprivedStatus) {
                    this.boardMembers.members.splice(index, 1)
                }
            }
        }
    }
}
</script>
